import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { Event } from "@attendik/types/schemas";

import { api, ApiError } from "../api/client.js";
import { useAuth } from "../store/auth.js";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; events: Event[] }
  | { status: "error"; message: string };

function formatRange(activeFrom: string, activeTo: string): string {
  const from = new Date(activeFrom);
  const to = new Date(activeTo);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${activeFrom} – ${activeTo}`;
  }

  return `${from.toLocaleString()} – ${to.toLocaleString()}`;
}

export function EventsPage(): JSX.Element {
  const navigate = useNavigate();
  const { operator, logout } = useAuth();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    api
      .getEvents()
      .then((events) => {
        if (!cancelled) {
          setState({ status: "ready", events });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          error instanceof ApiError
            ? error.message
            : "Could not load events. Check your connection.";
        setState({ status: "error", message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Logout must always clear local session, even if the API call fails.
    } finally {
      logout();
      navigate("/login");
    }
  }, [logout, navigate]);

  const openEvent = useCallback(
    (eventId: string): void => {
      navigate(`/scan/${eventId}`);
    },
    [navigate],
  );

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-slate-100">
      <header className="flex items-center justify-between gap-4 border-b border-slate-800 bg-slate-900 px-4 py-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">Attendik Scan</p>
          <p className="text-lg font-semibold">{operator?.name ?? "Operator"}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            void handleLogout();
          }}
          className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800"
        >
          Log out
        </button>
      </header>

      <main className="flex-1 px-4 py-6">
        <h1 className="mb-4 text-xl font-bold">Active events</h1>

        {state.status === "loading" && (
          <p className="text-slate-400" role="status">
            Loading events…
          </p>
        )}

        {state.status === "error" && (
          <p className="text-red-400" role="alert">
            {state.message}
          </p>
        )}

        {state.status === "ready" && state.events.length === 0 && (
          <p className="text-slate-400">No active events assigned to you.</p>
        )}

        {state.status === "ready" && state.events.length > 0 && (
          <ul className="flex flex-col gap-3">
            {state.events.map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => {
                    openEvent(event.id);
                  }}
                  className="w-full rounded-xl border border-slate-800 bg-slate-900 px-4 py-4 text-left transition-colors hover:border-slate-600 hover:bg-slate-800"
                >
                  <p className="text-lg font-semibold text-slate-50">{event.name}</p>
                  <p className="mt-1 text-sm text-slate-400">
                    {formatRange(event.active_from, event.active_to)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
